import { useCallback, useEffect, useState } from "react"

import { useIsMutating, useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"
import { toast } from "sonner"

import { getNotificationIconUrl } from "@/hooks/use-favicon"
import { api } from "@/lib/api-client"
import { queryKeys } from "@/lib/query-keys"
import { useParserJobsStore } from "@/stores/parser-jobs-store"

import { useAIParserSession } from "./use-ai-parser-session"

function showBrowserNotification(title: string) {
  if (typeof window === "undefined" || !("Notification" in window)) return
  if (Notification.permission !== "granted") return

  try {
    const notification = new Notification(title, {
      icon: getNotificationIconUrl(),
      silent: false,
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
  } catch {
    // ignore
  }
}

type ParserJobProgress = {
  totalFiles: number
  processedFiles: number
  totalColumns: number
  currentFile: string | null
  currentStep: string
  percentage: number
  errors: string[]
  warnings: string[]
}

type ParserJobStatus = "pending" | "running" | "completed" | "failed" | "cancelled"

type ParserJob = {
  jobId: string
  sessionId: string
  userId: string
  status: ParserJobStatus
  progress: ParserJobProgress
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  errorMessage: string | null
}

interface RunParserParams {
  sessionId: string
  background?: boolean
  filenames?: string[]
}

interface RunParserResponse {
  success: boolean
  message: string
  sessionId: string
  jobId?: string
  status?: string
  result?: unknown
}

type RunParserApiResponse = {
  success: boolean
  message: string
  session_id: string
  job_id?: string
  status?: string
  result?: unknown
}

type JobStatusApiResponse = {
  job_id: string
  session_id: string
  user_id: string
  status: ParserJobStatus
  progress: {
    total_files: number
    processed_files: number
    total_columns: number
    current_file: string | null
    current_step: string
    percentage: number
    errors: string[]
    warnings: string[]
  }
  created_at: string
  started_at: string | null
  completed_at: string | null
  error_message: string | null
}

async function runParser(data: RunParserParams): Promise<RunParserResponse> {
  const { sessionId, background = false, filenames } = data
  const response = (await api.post("/parser/run", {
    session_id: sessionId,
    background,
    filenames,
  })) as RunParserApiResponse
  return {
    success: response.success,
    message: response.message,
    sessionId: response.session_id,
    jobId: response.job_id,
    status: response.status,
    result: response.result,
  }
}

async function getJobStatus(jobId: string): Promise<ParserJob | null> {
  const queryParams = new URLSearchParams()
  queryParams.set("job_id", jobId)

  try {
    const response = (await api.get(
      `/parser/job-status?${queryParams.toString()}`
    )) as JobStatusApiResponse
    return {
      jobId: response.job_id,
      sessionId: response.session_id,
      userId: response.user_id,
      status: response.status,
      progress: {
        totalFiles: response.progress.total_files,
        processedFiles: response.progress.processed_files,
        totalColumns: response.progress.total_columns,
        currentFile: response.progress.current_file,
        currentStep: response.progress.current_step,
        percentage: response.progress.percentage,
        errors: response.progress.errors,
        warnings: response.progress.warnings,
      },
      createdAt: response.created_at,
      startedAt: response.started_at,
      completedAt: response.completed_at,
      errorMessage: response.error_message,
    }
  } catch {
    return null
  }
}

async function cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
  return api.post("/parser/cancel-job", { job_id: jobId }) as Promise<{
    success: boolean
    message: string
  }>
}

export function useRunParserMutation() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentSessionId } = useAIParserSession()

  const addActiveJob = useParserJobsStore((state) => state.addActiveJob)
  const removeActiveJob = useParserJobsStore((state) => state.removeActiveJob)

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [activeJobSessionId, setActiveJobSessionId] = useState<string | null>(null)
  const [isPolling, setIsPolling] = useState(false)

  const { data: jobStatus } = useQuery({
    queryKey: ["parserJobStatus", activeJobId],
    queryFn: () => (activeJobId ? getJobStatus(activeJobId) : null),
    enabled: isPolling && !!activeJobId,
    refetchInterval: isPolling ? 2000 : false,
    staleTime: 0,
  })

  useEffect(() => {
    if (!jobStatus) return

    if (jobStatus.status === "completed") {
      setIsPolling(false)
      setActiveJobId(null)
      setActiveJobSessionId(null)
      removeActiveJob(jobStatus.jobId)
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(jobStatus.sessionId),
      })
      void queryClient.invalidateQueries({ queryKey: ["parserActiveJobs"] })

      const sessionId = jobStatus.sessionId
      const isOnSession = location.pathname === `/ai-parser/chat/${sessionId}`

      if (document.hidden) {
        showBrowserNotification(t("pages.ai_parser.toasts.parser_data_updated"))
      }

      toast.success(t("pages.ai_parser.toasts.parser_data_updated"), {
        duration: 10000,
        action: isOnSession
          ? undefined
          : {
              label: t("pages.ai_parser.toasts.view_session"),
              onClick: () => navigate(`/ai-parser/chat/${sessionId}`),
            },
      })
    } else if (jobStatus.status === "failed") {
      setIsPolling(false)
      setActiveJobId(null)
      setActiveJobSessionId(null)
      removeActiveJob(jobStatus.jobId)
      void queryClient.invalidateQueries({ queryKey: ["parserActiveJobs"] })
      toast.error(jobStatus.errorMessage || t("pages.ai_parser.toasts.parser_error"))
    } else if (jobStatus.status === "cancelled") {
      setIsPolling(false)
      setActiveJobId(null)
      setActiveJobSessionId(null)
      removeActiveJob(jobStatus.jobId)
      void queryClient.invalidateQueries({ queryKey: ["parserActiveJobs"] })
      toast.info(t("pages.ai_parser.toasts.parser_cancelled"))
    }
  }, [jobStatus, queryClient, removeActiveJob, t, location.pathname, navigate])

  const runParserMutation = useMutation({
    mutationFn: runParser,
    mutationKey: queryKeys.runParser(currentSessionId ?? ""),
    onSuccess: async (response) => {
      if (response.jobId) {
        setActiveJobId(response.jobId)
        setActiveJobSessionId(response.sessionId)
        setIsPolling(true)
        addActiveJob(response.jobId, response.sessionId)
        void queryClient.invalidateQueries({ queryKey: ["parserActiveJobs"] })
        toast.info(t("pages.ai_parser.toasts.parser_started"))
      } else {
        await queryClient.invalidateQueries({
          queryKey: queryKeys.fullSession(response.sessionId),
        })
        toast.success(t("pages.ai_parser.toasts.parser_data_updated"))
      }
    },
    onError: () => {
      toast.error(t("pages.ai_parser.toasts.parser_error"))
    },
  })

  const cancelJobMutation = useMutation({
    mutationFn: cancelJob,
  })

  const isMutating = useIsMutating({
    mutationKey: queryKeys.runParser(currentSessionId ?? ""),
  })

  const isRunParserPending =
    isMutating > 0 || (isPolling && activeJobSessionId === currentSessionId)

  const startBackgroundJob = useCallback(
    async (sessionId: string) => {
      return runParserMutation.mutateAsync({ sessionId, background: true })
    },
    [runParserMutation]
  )

  const cancelCurrentJob = useCallback(async () => {
    if (activeJobId && activeJobSessionId === currentSessionId) {
      return cancelJobMutation.mutateAsync(activeJobId)
    }
  }, [activeJobId, activeJobSessionId, currentSessionId, cancelJobMutation])

  return {
    runParserMutation,
    isRunParserPending,
    cancelCurrentJob,
    startBackgroundJob,
    jobStatus,
    isPolling,
    activeJobSessionId,
  }
}
